import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Vote, Plus, BarChart3, Settings } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useEmailAuth } from '../../hooks/useEmailAuth';
import { useVoteWorker } from '../../hooks/useVoteWorker';
import { getSurveyCategories, getUserVotes, submitVote, getCategoryVoteResults, getArchivedSurveyCategories, deleteArchivedSurveyCategory } from '../../services/surveyService';
import { isSupabaseConfigured } from '../../config/supabase';
import SurveyCategoryManager from '../survey/SurveyCategoryManager';
import VotingInterface from '../survey/VotingInterface';
import VoteResults from '../survey/VoteResults';
import OverallResults from '../survey/OverallResults';
import ViewerModal from '../viewer/ViewerModal';

const SurveyTab = ({ allMovies, isAdmin }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { isUserAuthenticated, getCurrentUser } = useEmailAuth();
  const { calculateOverallResults, cleanup } = useVoteWorker();
  const calculateOverallResultsRef = useRef(calculateOverallResults);
  
  // Update ref when function changes
  useEffect(() => {
    calculateOverallResultsRef.current = calculateOverallResults;
  }, [calculateOverallResults]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [surveyCategories, setSurveyCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [userVotes, setUserVotes] = useState({});
  const [voteResults, setVoteResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('vote'); // 'vote', 'results', or 'archive'
  const [selectedMovieForDetails, setSelectedMovieForDetails] = useState(null);
  const [showMovieModal, setShowMovieModal] = useState(false);
  
  // Track which categories have loaded their content (movie_ids)
  const [loadedCategoryContent, setLoadedCategoryContent] = useState(new Set());
  const [categoryContentLoading, setCategoryContentLoading] = useState(new Set());
  const [archivedCategories, setArchivedCategories] = useState([]);

  // Using shared email auth hook for user authentication

  const loadSurveyData = useCallback(async (showLoadingScreen = true) => {
    console.log('loadSurveyData called - refreshing categories', { showLoadingScreen });
    
    // Only show loading screen on initial load, not on refresh after delete
    if (showLoadingScreen) {
      setLoading(true);
    }
    
    try {
      const { data: categories, error } = await getSurveyCategories();
      if (error) {
        console.error('Error loading survey categories:', error);
        setSurveyCategories([]); // Set empty array on error
      } else {
        console.log('Categories loaded:', categories?.length || 0);
        setSurveyCategories(categories || []);
        if (categories && categories.length > 0 && !selectedCategory) {
          setSelectedCategory(categories[0]);
        }
      }
    } catch (err) {
      console.error('Error loading survey data:', err);
      setSurveyCategories([]); // Set empty array on error
    } finally {
      console.log('Survey data loading complete');
      if (showLoadingScreen) {
        setLoading(false);
      }
    }
  }, [selectedCategory]); // Only depend on selectedCategory

  const loadUserVotes = async (userInfo = null) => {
    console.log('loadUserVotes called with:', { userInfo, getCurrentUserType: typeof getCurrentUser });
    
    const currentUser = userInfo || getCurrentUser();
    
    if (!currentUser) return;
    
    try {
      // For lazy loading, we only load user votes, not full categories
      // Categories are loaded separately by the header loading system
      console.log('🔄 Loading user votes only (lazy loading mode)');
      
      // Load user votes across all categories if user is provided
      const userEmail = currentUser.email || currentUser.userEmail;
      const votesCacheKey = `user_votes_cache_${userEmail}`;
      const cachedVotes = JSON.parse(localStorage.getItem(votesCacheKey) || '{}');
      const votesCacheAge = Date.now() - (cachedVotes.timestamp || 0);
      const votesCacheValid = votesCacheAge < 2 * 60 * 1000; // 2 minutes
      
      let userVotesData;
      
      if (votesCacheValid && cachedVotes.data) {
        console.log('✅ Using cached user votes (age:', Math.round(votesCacheAge / 1000), 'seconds)');
        userVotesData = cachedVotes.data;
      } else {
        const { queuedSupabaseOperation } = await import('../../utils/supabaseWrapper');
        userVotesData = await queuedSupabaseOperation.select(
          'survey_votes',
          `select=*&user_email=eq.${encodeURIComponent(userEmail)}`,
          null,
          `Load user votes for ${userEmail}`
        );
        
        // Cache the results
        try {
          localStorage.setItem(votesCacheKey, JSON.stringify({
            data: userVotesData,
            timestamp: Date.now()
          }));
        } catch (cacheError) {
          console.warn('⚠️ Failed to cache user votes:', cacheError);
        }
      }
      
      // Process user votes by category and movie
      const userVotesByCategory = {};
      if (userVotesData.length > 0) {
        for (let i = 0; i < userVotesData.length; i++) {
          const vote = userVotesData[i];
          if (!userVotesByCategory[vote.category_id]) {
            userVotesByCategory[vote.category_id] = {};
          }
          userVotesByCategory[vote.category_id][vote.movie_id] = vote.vote_value;
        }
      }
      
      // Only set user votes, don't overwrite categories
      setUserVotes(userVotesByCategory);
      
      console.log('✅ User votes loaded:', {
        userVotes: Object.keys(userVotesByCategory).length,
        totalVotes: userVotesData.length
      });
    } catch (err) {
      console.error('Error loading user votes:', err);
      
      // Fallback to original method
      try {
        const { data: votes, error } = await getUserVotes(currentUser);
        if (error) {
          console.error('Error loading user votes:', error);
        } else {
          // Convert votes array to object keyed by category_id and movie_id
          const votesMap = {};
          votes?.forEach(vote => {
            if (!votesMap[vote.category_id]) {
              votesMap[vote.category_id] = {};
            }
            votesMap[vote.category_id][vote.movie_id] = vote.vote_value;
          });
          setUserVotes(votesMap);
        }
      } catch (fallbackError) {
        console.error('Fallback vote loading failed:', fallbackError);
      }
    }
  };

  // Lazy loading - load category headers first, content on-demand
  useEffect(() => {
    console.log('Starting lazy survey loading - headers only');
    
    const loadCategoryHeaders = async () => {
      try {
        setLoading(true);
        
        // Load only category headers (lightweight)
        const { batchLoadSurveyCategoryHeaders } = await import('../../services/batchDataLoader');
        const { activeCategories, archivedCategories, error } = await batchLoadSurveyCategoryHeaders();
        
        if (error) {
          console.error('❌ Error loading category headers:', error);
        } else {
          console.log('✅ Category headers loaded:', {
            active: activeCategories.length,
            archived: archivedCategories.length
          });
          
          setSurveyCategories(activeCategories);
          setArchivedCategories(archivedCategories);
          
          
          // Temporarily disable preloading for debugging
          // setTimeout(() => {
          //   preloadPopularCategories();
          // }, 100);
        }
        
        setLoading(false); // Show UI immediately with headers
        
      } catch (err) {
        console.error('Error loading category headers:', err);
        setLoading(false);
        
        // Fallback to full loading
        loadSurveyData(true);
      }
    };
    
    loadCategoryHeaders();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are deliberately narrow; adding them re-runs this effect on every render
  }, []); // Only run on mount

  // State for overall results with Web Worker support
  const [overallResults, setOverallResults] = useState({});
  const [overallResultsLoading, setOverallResultsLoading] = useState(false);
  
  // Calculate overall results using Web Worker when needed
  useEffect(() => {
    if (view !== 'results' || selectedCategory !== 'overall') {
      return; // Don't calculate if not viewing overall results
    }
    
    const calculateResults = async () => {
      setOverallResultsLoading(true);
      
      try {
        console.log('🔄 Loading all category vote results for overall calculation...');
        console.log('📊 Available categories:', surveyCategories.map(c => ({ id: c.id, name: c.name, is_archived: c.is_archived })));
        
        // Load vote results for ALL active categories, not just visited ones
        const activeCategories = surveyCategories.filter(cat => !cat.is_archived);
        console.log('📊 Active categories for overall results:', activeCategories.map(c => c.name));
        
        if (activeCategories.length === 0) {
          console.warn('⚠️ No active categories found for overall results');
          setOverallResults({});
          return;
        }
        
        const allVoteResults = {};
        const fullyLoadedCategories = [];
        
        // Get current user email for loading category content
        const currentUser = getCurrentUser();
        const userEmail = currentUser?.email || currentUser?.userEmail;
        
        // First, ensure all categories have their full content loaded
        for (const category of activeCategories) {
          console.log(`🔄 Ensuring category content is loaded: ${category.name}`);
          
          let fullCategory = category;
          
          // If category doesn't have movie_ids, load its content
          if (!category.movie_ids) {
            console.log(`📥 Loading content for category: ${category.name}`);
            try {
              const { batchLoadCategoryContent } = await import('../../services/batchDataLoader');
              const result = await batchLoadCategoryContent(category.id, userEmail);
              if (result && result.category) {
                fullCategory = result.category;
                
                // Update the category in surveyCategories state
                setSurveyCategories(prev => prev.map(c => 
                  c.id === category.id ? { ...c, ...fullCategory } : c
                ));
                
                console.log(`✅ Content loaded for category ${category.name}:`, fullCategory.movie_ids?.length || 0, 'movies');
              } else {
                console.warn(`⚠️ Failed to load content for category: ${category.name}`);
                continue;
              }
            } catch (error) {
              console.warn(`❌ Error loading content for category ${category.name}:`, error);
              continue;
            }
          }
          
          fullyLoadedCategories.push(fullCategory);
        }
        
        // Now load vote results for each fully loaded category
        for (const category of fullyLoadedCategories) {
          try {
            console.log(`🔄 Loading votes for category: ${category.name}`);
            const { batchLoadCategoryVotes } = await import('../../services/batchDataLoader');
            const { votesByCategory, error } = await batchLoadCategoryVotes([category.id]);
            
            if (error) {
              console.warn(`❌ Error loading votes for category ${category.name}:`, error);
            } else if (votesByCategory[category.id]) {
              allVoteResults[category.id] = votesByCategory[category.id];
              console.log(`✅ Loaded votes for category ${category.name}:`, Object.keys(votesByCategory[category.id]).length, 'movies');
            } else {
              console.log(`ℹ️ No votes found for category: ${category.name}`);
            }
          } catch (error) {
            console.warn(`❌ Failed to load votes for category ${category.name}:`, error);
          }
        }
        
        console.log('🔄 Calculating overall results with Web Worker...');
        console.log('📊 All vote results loaded:', Object.keys(allVoteResults));
        console.log('📊 Total categories with votes:', Object.keys(allVoteResults).length);
        
        if (Object.keys(allVoteResults).length === 0) {
          console.warn('⚠️ No vote results found for any category');
          setOverallResults({});
          return;
        }
        
        const results = await calculateOverallResultsRef.current(allVoteResults);
        console.log('🔍 Web Worker returned results:', results);
        console.log('🔍 Results type:', typeof results);
        console.log('🔍 Results keys:', results ? Object.keys(results) : 'N/A');
        
        // Ensure we always set an object, never undefined
        setOverallResults(results || {});
        console.log('✅ Overall results calculated for', Object.keys(results || {}).length, 'movies');
      } catch (error) {
        console.error('❌ Error calculating overall results:', error);
        setOverallResults({});
      } finally {
        setOverallResultsLoading(false);
      }
    };
    
    // Only calculate if we have categories to work with
    if (surveyCategories.length > 0) {
      // Debounce calculation to avoid excessive recalculations
      const timeoutId = setTimeout(calculateResults, 100);
      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are deliberately narrow; adding them re-runs this effect on every render
  }, [view, selectedCategory, surveyCategories.length]); // Only depend on categories length to avoid excessive re-runs
  
  // Cleanup worker on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Load user votes when authentication changes - MOVED AFTER FUNCTION DEFINITIONS
  useEffect(() => {
    console.log('Auth state changed, loading votes:', { 
      isUserAuthenticated, 
      getCurrentUserType: typeof getCurrentUser,
      currentUser: getCurrentUser()
    });
    
    if (isUserAuthenticated) {
      loadUserVotes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are deliberately narrow; adding them re-runs this effect on every render
  }, [isUserAuthenticated]); // Only depend on auth state

  // Debug effect to monitor archivedCategories state changes
  useEffect(() => {
    console.log('🔄 SurveyTab: archivedCategories state changed:', {
      length: archivedCategories.length,
      items: archivedCategories.map(c => ({ id: c.id, name: c.name, is_archived: c.is_archived }))
    });
  }, [archivedCategories]);

  // Preload popular categories when survey categories are loaded
  useEffect(() => {
    if (surveyCategories.length > 0) {
      // Small delay to avoid blocking initial render
      setTimeout(() => {
        preloadPopularCategories();
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are deliberately narrow; adding them re-runs this effect on every render
  }, [surveyCategories]);

  const loadVoteResults = async (categoryId, forceReload = false) => {
    // Skip if results already cached and not forcing reload
    if (!forceReload && voteResults[categoryId]) {
      console.log('✅ Using cached vote results for category:', categoryId);
      return;
    }

    console.log('🔄 loadVoteResults called for category:', categoryId, { forceReload });
    
    // Set loading state for this specific category
    setVoteResults(prev => ({
      ...prev,
      [categoryId]: prev[categoryId] || null // Keep existing data while loading
    }));

    try {
      // Use batch loader for better performance
      const { batchLoadCategoryVotes } = await import('../../services/batchDataLoader');
      const { votesByCategory, error } = await batchLoadCategoryVotes([categoryId]);
      
      const results = votesByCategory[categoryId] || {};
      
      console.log('📊 Vote results response:', { 
        categoryId, 
        results, 
        error,
        resultsKeys: Object.keys(results),
        resultsCount: Object.keys(results).length,
        sampleResult: Object.values(results)[0]
      });
      
      if (error) {
        console.error('❌ Error loading vote results:', error);
        // Keep any existing cached data on error
      } else {
        setVoteResults(prev => {
          const newResults = {
            ...prev,
            [categoryId]: results
          };
          console.log('✅ Updated voteResults state:', {
            categoryId,
            newResults,
            categoryResultsCount: Object.keys(results).length
          });
          return newResults;
        });
      }
    } catch (err) {
      console.error('❌ Error in batch vote loading:', err);
      
      // Fallback to original method
      try {
        const { data: results, error } = await getCategoryVoteResults(categoryId);
        if (!error && results) {
          setVoteResults(prev => ({
            ...prev,
            [categoryId]: results
          }));
          console.log('🔄 Used fallback method for vote results:', categoryId);
        }
      } catch (fallbackErr) {
        console.error('❌ Fallback vote loading failed:', fallbackErr);
        
        // Final fallback to localStorage cache
        try {
          const cacheKey = `vote_results_${categoryId}`;
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            const isStale = Date.now() - timestamp > 5 * 60 * 1000; // 5 minutes
            
            if (!isStale) {
              console.log('🔄 Using localStorage fallback for vote results:', categoryId);
              setVoteResults(prev => ({
                ...prev,
                [categoryId]: data
              }));
            }
          }
        } catch (cacheError) {
          console.warn('⚠️ Cache fallback failed:', cacheError);
        }
      }
    }
  };

  // Preload content for commonly accessed categories
  const preloadPopularCategories = async () => {
    if (surveyCategories.length === 0) return;

    // Preload first 3 categories (most likely to be viewed)
    const categoriesToPreload = surveyCategories.slice(0, 3);
    const currentUser = getCurrentUser();
    const userEmail = currentUser?.email || currentUser?.userEmail;
    
    console.log('🚀 Preloading content for popular categories:', categoriesToPreload.map(c => c.name));
    
    // Load category content in background without blocking UI
    const preloadPromises = categoriesToPreload.map(async (category) => {
      const categoryId = category.id;
      
      // Skip if already loaded or loading
      if (loadedCategoryContent.has(categoryId) || categoryContentLoading.has(categoryId)) {
        return;
      }
      
      try {
        const { batchLoadCategoryContent } = await import('../../services/batchDataLoader');
        const { category: fullCategory, userVotes: categoryUserVotes, error } = await batchLoadCategoryContent(categoryId, userEmail);
        
        if (!error && fullCategory) {
          // Update category with full data
          setSurveyCategories(prev => prev.map(cat => 
            cat.id === categoryId ? { ...cat, ...fullCategory } : cat
          ));
          
          // Update user votes
          if (Object.keys(categoryUserVotes).length > 0) {
            setUserVotes(prev => ({
              ...prev,
              [categoryId]: categoryUserVotes
            }));
          }
          
          // Mark as loaded
          setLoadedCategoryContent(prev => {
            const newSet = new Set([...prev, categoryId]);
            console.log(`✅ PRELOAD: Marked category as loaded: ${category.name} (${categoryId})`, 'New set:', Array.from(newSet));
            return newSet;
          });
        }
      } catch (error) {
        console.warn(`⚠️ Failed to preload category ${category.name}:`, error);
      }
    });

    // Don't await - let them load in background
    Promise.allSettled(preloadPromises).then(() => {
      console.log('✅ Popular categories preload completed');
    });
  };

  const loadArchivedCategories = async () => {
    try {
      console.log('🔍 SurveyTab: Loading archived categories...');
      const { data, error } = await getArchivedSurveyCategories();
      console.log('📊 SurveyTab: Archived categories response:', { data, error, count: data?.length });
      console.log('📋 SurveyTab: Raw data details:', data);
      
      if (error) {
        console.error('❌ Error loading archived categories:', error);
        setArchivedCategories([]);
        return;
      }
      
      const categoriesArray = data || [];
      console.log('🔄 SurveyTab: About to set state with:', categoriesArray);
      setArchivedCategories(categoriesArray);
      console.log('✅ SurveyTab: Set archived categories state:', categoriesArray.length, 'items');
      console.log('🎯 SurveyTab: Categories set:', categoriesArray.map(c => ({ id: c.id, name: c.name, is_archived: c.is_archived })));
      
      // Force a re-render check
      setTimeout(() => {
        console.log('🔍 SurveyTab: State check after timeout - this might show stale state due to closure');
      }, 100);
    } catch (err) {
      console.error('❌ Error in loadArchivedCategories:', err);
      setArchivedCategories([]);
    }
  };

  const handleVote = async (categoryId, movieId, voteValue) => {
    const currentUser = getCurrentUser();
    
    if (!currentUser) {
      alert('Please sign in to vote on movies.');
      return;
    }

    try {
      const voteData = {
        categoryId,
        movieId,
        voteValue,
        userEmail: currentUser.email,
        userName: currentUser.name
      };

      // Add userId only for fully authenticated users
      if (currentUser.userId) {
        voteData.userId = currentUser.userId;
      }

      const { error } = await submitVote(voteData);

      if (error) {
        console.error('Error submitting vote:', error);
        // Could show toast notification here
      } else {
        // Update local state
        setUserVotes(prev => ({
          ...prev,
          [categoryId]: {
            ...prev[categoryId],
            [movieId]: voteValue
          }
        }));
        
        // Invalidate cached vote results for this category since data changed
        setVoteResults(prev => {
          const updated = { ...prev };
          delete updated[categoryId]; // Remove cached results
          return updated;
        });
        
        // Clear localStorage cache for this category and batch cache
        try {
          const cacheKey = `vote_results_${categoryId}`;
          localStorage.removeItem(cacheKey);
          
          // Also invalidate batch cache
          const { invalidateVoteResultsCache } = await import('../../services/batchDataLoader');
          invalidateVoteResultsCache();
          
          console.log('🗑️ Cleared vote results cache for category:', categoryId);
        } catch (cacheError) {
          console.warn('⚠️ Failed to clear vote results cache:', cacheError);
        }
        
        // Refresh results if viewing results (will reload from server)
        if (view === 'results') {
          loadVoteResults(categoryId, true); // Force reload
        }
      }
    } catch (err) {
      console.error('Error submitting vote:', err);
    }
  };

  const handleCategorySelect = async (category) => {
    console.log('🔄 handleCategorySelect called with:', category);
    console.log('🔍 Category has movie_ids on selection:', !!category.movie_ids, category.movie_ids?.length || 0);
    
    setSelectedCategory(category);
    
    // Skip content loading for overall results
    if (category === 'overall') {
      console.log('✅ Selected overall results');
      return;
    }
    
    const categoryId = category.id;
    
    // Check if we need to load category content (movie_ids)
    const wasMarkedAsLoaded = loadedCategoryContent.has(categoryId);
    const isCurrentlyLoading = categoryContentLoading.has(categoryId);
    const needsLoading = !category.movie_ids && !isCurrentlyLoading;
    
    console.log('🔍 Category loading check:', {
      categoryName: category.name,
      hasMovieIds: !!category.movie_ids,
      wasMarkedAsLoaded,
      isCurrentlyLoading,
      needsLoading
    });
    
    // If category is marked as loaded but doesn't have movie_ids, reset the loaded status
    if (wasMarkedAsLoaded && !category.movie_ids) {
      console.log('🔄 Resetting loaded status for category:', category.name);
      setLoadedCategoryContent(prev => {
        const newSet = new Set(prev);
        newSet.delete(categoryId);
        return newSet;
      });
    }
    
    if (needsLoading) {
      console.log('🔄 Starting to load category content for:', category.name);
      
      // Mark as loading
      setCategoryContentLoading(prev => new Set([...prev, categoryId]));
      
      try {
        const { batchLoadCategoryContent } = await import('../../services/batchDataLoader');
        const currentUser = getCurrentUser();
        const userEmail = currentUser?.email || currentUser?.userEmail;
        
        const result = await batchLoadCategoryContent(categoryId, userEmail);
        
        const { category: fullCategory, userVotes: categoryUserVotes, error } = result;
        
        if (error) {
          console.error('❌ Error loading category content:', error);
        } else {
          
          // Update the category in our state with full data
          setSurveyCategories(prev => {
            const updated = prev.map(cat => 
              cat.id === categoryId ? { ...cat, ...fullCategory } : cat
            );
            return updated;
          });
          
          // Update selectedCategory with the full category data
          setSelectedCategory(prevSelected => {
            if (prevSelected && prevSelected.id === categoryId) {
              const updatedCategory = { ...prevSelected, ...fullCategory };
              console.log('✅ Updated selectedCategory with full data:', updatedCategory.name, 'movie_ids:', updatedCategory.movie_ids?.length);
              return updatedCategory;
            }
            return prevSelected;
          });
          
          // Update user votes for this category
          if (categoryUserVotes && Object.keys(categoryUserVotes).length > 0) {
            setUserVotes(prev => ({
              ...prev,
              [categoryId]: categoryUserVotes
            }));
          }
          
          // Mark as loaded
          setLoadedCategoryContent(prev => {
            const newSet = new Set([...prev, categoryId]);
            console.log('✅ Marked category as loaded:', categoryId, 'New set:', Array.from(newSet));
            return newSet;
          });
        }
      } catch (err) {
        console.error('❌ Error loading category content:', err);
      } finally {
        // Remove from loading set
        setCategoryContentLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(categoryId);
          return newSet;
        });
      }
    }
    
    // Load vote results if viewing results
    if (view === 'results') {
      console.log('🔄 Loading vote results for category:', categoryId);
      loadVoteResults(categoryId);
    }
  };

  const handleViewChange = (newView) => {
    console.log('SurveyTab: View changing from', view, 'to', newView);
    setView(newView);
    
    // If switching to results view and no category is selected, default to overall
    if (newView === 'results' && !selectedCategory) {
      setSelectedCategory('overall');
    } else if (newView === 'archive') {
      console.log('SurveyTab: Switching to archive view, loading archived categories...');
      // Load archived categories when switching to archive view
      loadArchivedCategories();
      setSelectedCategory(null);
    }
    
    if (newView === 'results' && selectedCategory && selectedCategory !== 'overall') {
      console.log('🔄 Loading vote results on view change for category:', selectedCategory.id);
      loadVoteResults(selectedCategory.id);
    }
  };

  // eslint-disable-next-line no-unused-vars -- dead code, kept for now
  const getMovieById = (movieId) => {
    return allMovies.find(movie => movie.id === movieId);
  };

  const handleMovieClick = (movie) => {
    setSelectedMovieForDetails(movie);
    setShowMovieModal(true);
  };

  const closeMovieModal = () => {
    setShowMovieModal(false);
    setSelectedMovieForDetails(null);
  };

  const handleDeleteArchivedCategory = async (category) => {
    if (!window.confirm(`Permanently delete the archived survey "${category.name}"? This action cannot be undone and will delete all associated votes.`)) {
      return;
    }

    try {
      const { error } = await deleteArchivedSurveyCategory(category.id);
      if (error) {
        console.error('Error deleting archived category:', error);
        alert('Failed to delete archived survey. Please try again.');
        return;
      }

      // If we were viewing this category's results, go back to archive list
      if (selectedCategory && selectedCategory.id === category.id) {
        setSelectedCategory(null);
      }

      // Refresh archived categories list
      loadArchivedCategories();
      
      console.log('Archived category deleted successfully');
    } catch (err) {
      console.error('Error in handleDeleteArchivedCategory:', err);
      alert('Failed to delete archived survey. Please try again.');
    }
  };

  // Handle email-only authentication (using shared hook)

  // Removed duplicate definitions - moved above

  // Debug logging for flickering issue
  console.log('SurveyTab Render Debug:', {
    isAdmin,
    isSupabaseConfigured: isSupabaseConfigured(),
    authLoading,
    loading,
    isAuthenticated,
    user: user ? { id: user.id, email: user.email } : null,
    currentUser: getCurrentUser(),
    isUserAuthenticated,
    surveyCategories: surveyCategories.length,
    selectedCategory: selectedCategory?.name,
    view,
    archivedCategoriesCount: archivedCategories.length
  });

  // Check if Supabase is configured
  if (!isSupabaseConfigured()) {
    return (
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-6">
        <div className="text-center py-12">
          <Settings className="w-16 h-16 mx-auto text-red-400 mb-4" />
          <h3 className="text-xl font-semibold text-red-300 mb-4">Supabase Not Configured</h3>
          <p className="text-gray-300 mb-6">
            The Survey feature requires Supabase for authentication and database storage.<br/>
            Please configure your Supabase credentials to use this feature.
          </p>
          <button
            onClick={() => window.location.hash = '#settings'}
            className="bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 rounded-lg font-bold transition-colors flex items-center gap-2 mx-auto"
          >
            <Settings size={16} />
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  // Only show loading if we're actually loading survey data
  // Don't block on authLoading for non-admin users since they can use email auth
  // Also don't show loading if auth is still loading for non-admin users
  if (loading && !(authLoading && !isAdmin)) {
    return (
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-6">
        <div className="text-center py-8">
          <Vote className="w-12 h-12 mx-auto text-orange-400 animate-pulse mb-4" />
          <p className="text-gray-300">Loading survey data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-6">
      {/* Header */}
      <div className="mb-6">
        {/* Desktop Layout - Side by side */}
        <div className="hidden lg:flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-orange-300 mb-2 flex items-center gap-2">
            <Vote size={24} />
            🗳️ Movie Survey
          </h2>
                      <p className="text-orange-200">
              {view === 'vote'
                ? "This year's movie survey."
                : view === 'results'
                ? "Results of this year's survey."
                : view === 'archive'
                ? "Results of past surveys."
                : "Vote on movie categories and see what the community thinks"
              }
            </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-gray-700 rounded-lg border border-orange-500">
            <button
              onClick={() => handleViewChange('vote')}
              className={`px-4 py-2 rounded-l-lg font-semibold transition-colors flex items-center gap-2 ${
                view === 'vote'
                  ? 'bg-orange-600 text-white'
                  : 'text-orange-300 hover:text-orange-100'
              }`}
            >
              <Vote size={16} />
              Vote
            </button>
            <button
              onClick={() => handleViewChange('results')}
              className={`px-4 py-2 font-semibold transition-colors flex items-center gap-2 ${
                view === 'results'
                  ? 'bg-orange-600 text-white'
                  : 'text-orange-300 hover:text-orange-100'
              }`}
            >
              <BarChart3 size={16} />
              Results
            </button>
            <button
              onClick={() => handleViewChange('archive')}
              className={`px-4 py-2 rounded-r-lg font-semibold transition-colors flex items-center gap-2 ${
                view === 'archive'
                  ? 'bg-orange-600 text-white'
                  : 'text-orange-300 hover:text-orange-100'
              }`}
            >
              📦 Past Results
            </button>
          </div>

          {/* Admin Controls */}
          {isAdmin && (
            <button
              onClick={() => setShowCategoryManager(true)}
              className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
            >
              <Settings size={16} />
              Manage Categories
            </button>
          )}
          </div>
        </div>

        {/* Mobile Layout - Stacked */}
        <div className="lg:hidden">
          {/* Title Section */}
          <div className="mb-4">
            <h2 className="text-xl font-bold text-orange-300 mb-2 flex items-center gap-2">
              <Vote size={20} />
              🗳️ Movie Survey
            </h2>
            <p className="text-orange-200 text-sm">
              {view === 'vote'
                ? "This year's movie survey."
                : view === 'results'
                ? "Results of this year's survey."
                : view === 'archive'
                ? "Results of past surveys."
                : "Vote on movie categories and see what the community thinks"
              }
            </p>
          </div>
          
          {/* Controls Section */}
          <div className="space-y-3">
            {/* View Toggle - Full width on mobile */}
            <div className="flex bg-gray-700 rounded-lg border border-orange-500">
              <button
                onClick={() => handleViewChange('vote')}
                className={`flex-1 px-3 py-2 rounded-l-lg font-semibold transition-colors flex items-center justify-center gap-2 text-sm ${
                  view === 'vote'
                    ? 'bg-orange-600 text-white'
                    : 'text-orange-300 hover:text-orange-100'
                }`}
              >
                <Vote size={14} />
                Vote
              </button>
              <button
                onClick={() => handleViewChange('results')}
                className={`flex-1 px-3 py-2 font-semibold transition-colors flex items-center justify-center gap-2 text-sm ${
                  view === 'results'
                    ? 'bg-orange-600 text-white'
                    : 'text-orange-300 hover:text-orange-100'
                }`}
              >
                <BarChart3 size={14} />
                Results
              </button>
              <button
                onClick={() => handleViewChange('archive')}
                className={`flex-1 px-3 py-2 rounded-r-lg font-semibold transition-colors flex items-center justify-center gap-2 text-sm ${
                  view === 'archive'
                    ? 'bg-orange-600 text-white'
                    : 'text-orange-300 hover:text-orange-100'
                }`}
              >
                📦 Past
              </button>
            </div>

            {/* Admin Controls - Full width on mobile */}
            {isAdmin && (
              <button
                onClick={() => setShowCategoryManager(true)}
                className="w-full bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
              >
                <Settings size={16} />
                Manage Categories
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category Selection */}
      {surveyCategories.length > 0 && view !== 'archive' && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-orange-300 font-bold text-lg">🪓 Choose a category:</h3>
            <div className="h-px bg-gradient-to-r from-orange-500 to-transparent flex-1"></div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Overall Results Option */}
            {view === 'results' && (
              <button
                onClick={() => handleCategorySelect('overall')}
                className={`group relative overflow-hidden rounded-xl p-4 font-bold transition-all duration-300 transform hover:scale-105 ${
                  selectedCategory === 'overall'
                    ? 'bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 text-white shadow-2xl shadow-purple-500/30 ring-2 ring-purple-400'
                    : 'bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800 text-purple-300 hover:from-purple-900 hover:via-purple-800 hover:to-purple-900 border border-purple-500/50 hover:border-purple-400 shadow-lg hover:shadow-purple-500/20'
                }`}
              >
                <div className="relative z-10 flex flex-col items-center text-center">
                  <div className="text-3xl mb-2 group-hover:animate-pulse">📊</div>
                  <div className="font-bold text-sm">Overall Results</div>
                  <div className="text-xs opacity-75 mt-1">All categories combined</div>
                </div>
                {selectedCategory !== 'overall' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/0 via-purple-400/20 to-purple-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
              </button>
            )}
            
            {/* Individual Categories */}
            {surveyCategories.map(category => {
              const movieCount = Array.isArray(category.movie_ids) 
                ? category.movie_ids.length 
                : (typeof category.movie_ids === 'string' ? JSON.parse(category.movie_ids || '[]').length : 0);

              return (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category)}
                  disabled={categoryContentLoading.has(category.id)}
                  className={`group relative overflow-hidden rounded-xl p-4 font-bold transition-all duration-300 transform hover:scale-105 ${
                    selectedCategory?.id === category.id
                      ? 'bg-gradient-to-br from-orange-600 via-red-600 to-orange-700 text-white shadow-2xl shadow-orange-500/30 ring-2 ring-orange-400'
                      : categoryContentLoading.has(category.id)
                        ? 'bg-gradient-to-br from-gray-700 via-gray-600 to-gray-700 text-gray-400 cursor-not-allowed border border-gray-500/50'
                      : 'bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800 text-orange-300 hover:from-orange-900 hover:via-red-900 hover:to-orange-900 border border-orange-500/50 hover:border-orange-400 shadow-lg hover:shadow-orange-500/20'
                  }`}
                >
                  <div className="relative z-10 flex flex-col items-center text-center">
                    {categoryContentLoading.has(category.id) && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                      </div>
                    )}
                    
                    <div className="font-bold text-sm mb-1 leading-tight">
                      {category.name}
                    </div>
                    
                    {category.description && (
                      <div className="text-xs opacity-75 mb-3 leading-tight line-clamp-2">
                        {category.description}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-xs opacity-60">
                      <span>🎬 {movieCount}</span>
                      {view === 'vote' && (
                        <span>•</span>
                      )}
                      {view === 'vote' && !category.is_closed && (
                        <span className="text-green-400">🗳️ Open</span>
                      )}
                      {view === 'vote' && category.is_closed && (
                        <span className="text-red-400">🔒 Closed</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Hover effect overlay */}
                  {selectedCategory?.id !== category.id && !categoryContentLoading.has(category.id) && (
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-600/0 via-orange-400/20 to-orange-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  )}
                  
                  {/* Selected glow effect */}
                  {selectedCategory?.id === category.id && (
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 via-red-400/30 to-orange-400/20 animate-pulse"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Content Area */}
      {view === 'archive' ? (
        <div className="space-y-6">

          {/* Back to Archive List Button (when viewing specific results) */}
          {selectedCategory && (
            <div className="flex justify-start">
              <button
                onClick={() => setSelectedCategory(null)}
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
              >
                ← Back to Archive List
              </button>
            </div>
          )}

          {/* Show specific archived category results */}
          {selectedCategory ? (
            <div>
              {/* Category Header with Image */}
              {selectedCategory.header_image_url && (
                <div className="mb-6 relative rounded-lg overflow-hidden border-2 border-orange-500">
                  <img
                    src={selectedCategory.header_image_url}
                    alt={`${selectedCategory.name} header`}
                    className="w-full h-auto object-contain max-h-64 md:max-h-96"
                    style={{ minHeight: '120px' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h2 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">{selectedCategory.name}</h2>
                    {selectedCategory.description && (
                      <p className="text-gray-200 text-sm drop-shadow-md">{selectedCategory.description}</p>
                    )}
                    <div className="text-gray-300 text-xs mt-2 drop-shadow-md">
                      📅 Archived: {new Date(selectedCategory.archived_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              )}

              {/* Show archived survey results */}
              <VoteResults
                category={selectedCategory}
                movies={allMovies}
                voteResults={voteResults[selectedCategory.id]}
                loading={!voteResults[selectedCategory.id]}
              />
            </div>
          ) : (
            /* Show archive list when no category selected */
            (() => {
              console.log('🔍 Archive view render check:', {
                archivedCategoriesLength: archivedCategories.length,
                archivedCategories: archivedCategories,
                archivedCategoriesFirstItem: archivedCategories[0],
                showingEmptyMessage: archivedCategories.length === 0,
                view: view,
                selectedCategory: selectedCategory
              });
              
              return archivedCategories.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📦</div>
                  <h3 className="text-lg font-bold text-orange-300 mb-2">No Archived Surveys</h3>
                  <p className="text-orange-200">
                    Archived surveys will appear here when you archive closed surveys from the manage categories view.
                  </p>
                  <div className="text-xs text-gray-500 mt-4">
                    Debug: {archivedCategories.length} archived categories loaded
                  </div>
                  <button
                    onClick={loadArchivedCategories}
                    className="mt-4 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm"
                  >
                    🔄 Refresh Archived Surveys
                  </button>
                </div>
              ) : (
              <div>
                <div className="mb-4 text-center">
                  <button
                    onClick={loadArchivedCategories}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                  >
                    🔄 Refresh Archived Surveys
                  </button>
                  <div className="text-xs text-gray-500 mt-2">
                    Debug: {archivedCategories.length} archived categories loaded
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {archivedCategories.map(category => (
                  <div key={category.id} className="bg-gray-700 border border-orange-500 rounded-lg p-4">
                    {/* Header Image */}
                    {category.header_image_url && (
                      <div className="mb-4">
                        <img
                          src={category.header_image_url}
                          alt={`${category.name} header`}
                          className="w-full h-auto object-contain max-h-32 rounded border border-orange-500"
                        />
                      </div>
                    )}
                    
                    <div className="mb-4">
                      <h4 className="text-orange-200 font-bold text-lg">{category.name}</h4>
                      {category.description && (
                        <p className="text-gray-300 text-sm mt-1">{category.description}</p>
                      )}
                      <div className="text-gray-400 text-xs mt-2">
                        📅 Archived: {new Date(category.archived_at).toLocaleDateString()}
                      </div>
                      <div className="text-gray-400 text-xs">
                        🎬 {category.movie_ids?.length || 0} movies
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedCategory(category);
                          // Stay in archive view, don't switch to results view
                          loadVoteResults(category.id);
                        }}
                        className="flex-1 bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                      >
                        View Results
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteArchivedCategory(category)}
                          className="bg-red-600 hover:bg-red-500 text-white px-3 py-2 rounded-lg font-semibold transition-colors"
                          title="Delete archived survey"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </div>
              );
            })()
          )}
        </div>
      ) : surveyCategories.length === 0 ? (
        <div className="text-center py-12">
          <Vote className="w-16 h-16 mx-auto text-gray-500 mb-4" />
          <h3 className="text-xl font-semibold text-gray-400 mb-2">No Surveys Available</h3>
          <p className="text-gray-500 mb-4">
            {isAdmin 
              ? "Create your first survey category to get started!"
              : "Check back later when surveys are available."
            }
          </p>
          {isAdmin && (
            <button
              onClick={() => setShowCategoryManager(true)}
              className="bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 rounded-lg font-bold transition-colors flex items-center gap-2 mx-auto"
            >
              <Plus size={16} />
              Create Survey Category
            </button>
          )}
        </div>
      ) : selectedCategory ? (
        <div>
          {/* Category Header with Image */}
          {selectedCategory !== 'overall' && selectedCategory.header_image_url && (
            <div className="mb-6 relative rounded-lg overflow-hidden border-2 border-orange-500">
              <img
                src={selectedCategory.header_image_url}
                alt={`${selectedCategory.name} header`}
                className="w-full h-auto object-contain max-h-64 md:max-h-96"
                style={{ minHeight: '120px' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h2 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">{selectedCategory.name}</h2>
                {selectedCategory.description && (
                  <p className="text-gray-200 text-sm drop-shadow-md">{selectedCategory.description}</p>
                )}
              </div>
            </div>
          )}
          
          {view === 'vote' ? (
            <VotingInterface
              category={selectedCategory}
              movies={allMovies}
              userVotes={userVotes[selectedCategory.id] || {}}
              onVote={handleVote}
              isAuthenticated={isUserAuthenticated}
              currentUser={getCurrentUser()}
              onMovieClick={handleMovieClick}
            />
          ) : (
            (() => {
              // Handle Overall Results
              
              if (selectedCategory === 'overall') {
                console.log('📊 Rendering OverallResults component with memoized data');
        console.log('📊 Categories being passed to OverallResults:', surveyCategories.length);
        console.log('📊 Categories sample for OverallResults:', surveyCategories.slice(0, 2).map(c => ({ 
          id: c.id, 
          name: c.name, 
          hasMovieIds: !!c.movie_ids,
          movieIdsLength: c.movie_ids?.length || 0 
        })));
                return (
                  <OverallResults
                    categories={surveyCategories}
                    movies={allMovies}
                    overallResults={overallResults}
                    loading={loading || overallResultsLoading}
                    onMovieClick={handleMovieClick}
                  />
                );
              }
              
              // Handle Individual Category Results
              console.log('🎯 Rendering individual category results');
              const categoryResults = voteResults[selectedCategory.id];
              console.log('📊 VoteResults props debug:', {
                selectedCategoryId: selectedCategory.id,
                selectedCategoryName: selectedCategory.name,
                voteResults: voteResults,
                categoryResults: categoryResults,
                loading: !categoryResults,
                hasResults: categoryResults && Object.keys(categoryResults).length > 0
              });
              
              return (
                <VoteResults
                  category={selectedCategory}
                  movies={allMovies}
                  voteResults={categoryResults}
                  loading={!categoryResults}
                  onMovieClick={handleMovieClick}
                />
              );
            })()
          )}
        </div>
      ) : null}

      {/* Modals */}

      {isAdmin && (
        <SurveyCategoryManager
          isVisible={showCategoryManager}
          onClose={() => setShowCategoryManager(false)}
          allMovies={allMovies}
          onCategoryCreated={() => loadSurveyData(false)}
          existingCategories={surveyCategories}
          currentUser={getCurrentUser()}
        />
      )}

      {/* Movie Details Modal */}
      {selectedMovieForDetails && (
        <ViewerModal
          scheduledMovie={{
            movie: selectedMovieForDetails,
            timeSlot: null,
            date: null
          }}
          isVisible={showMovieModal}
          onClose={closeMovieModal}
          isAdmin={false}
          movieStatus="coming"
          viewers={[]}
          userName=""
          isSurveyMode={true}
        />
      )}

    </div>
  );
};

export default SurveyTab;
